// Minimal class-component error boundary. Catches render-time errors in
// children and renders a fallback instead of letting the error bubble
// up and unmount the entire React tree.
//
// Used around new/experimental UI - if it throws, the rest of the page
// still works. The default fallback is a small visible notice so a
// hidden chart failure doesn't look like missing data; pass `fallback`
// to override (including `<></>` for an intentional silent degrade).

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Custom fallback. Pass `<></>` for explicit silent degrade. Default
   *  is a small notice ("This panel had an error · reload"). */
  fallback?: ReactNode;
  /** Optional debug label for console output so it's clear which boundary
   *  caught the error when there's more than one on a page. */
  label?: string;
}

interface State {
  hasError: boolean;
}

function DefaultFallback({ label }: { label?: string }): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        padding: "14px 18px",
        margin: "12px 0",
        border: "1px solid var(--color-line)",
        borderRadius: 10,
        background: "var(--color-bg-card)",
        color: "var(--color-text-muted)",
        fontFamily: "var(--font-body)",
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      <div style={{ color: "var(--color-rose-hi)", fontWeight: 600, marginBottom: 4 }}>
        This panel hit an error.
      </div>
      <div>
        Reload the page to try again
        {label ? <> &middot; <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.7 }}>{label}</span></> : null}.
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console so it's visible when debugging - production
    // build doesn't include source maps inline so the stack trace will
    // reference the minified bundle, but the message + component stack
    // is still useful.
    // eslint-disable-next-line no-console
    console.error(`ErrorBoundary[${this.props.label ?? "unlabeled"}] caught:`, error, info.componentStack);
    window.dispatchEvent(new CustomEvent("damon:bark", { detail: { id: "error" } }));
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      // `fallback` may be explicitly `null` / `false` / fragment for a
      // silent degrade. Only fall through to the default notice when
      // the prop wasn't provided at all (i.e. is undefined).
      return this.props.fallback !== undefined
        ? this.props.fallback
        : <DefaultFallback label={this.props.label} />;
    }
    return this.props.children;
  }
}
